import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { gifts } from "../lib/api";
import BackToLifeGuide from "../components/BackToLifeGuide";
import {
  Sparkles,
  Gift,
  Check,
  ArrowRight,
  Heart,
  BookOpen,
  MessageCircleHeart,
  Mail,
  CalendarClock,
  EyeOff,
} from "lucide-react";

// Pricing mirrors backend GIFT_PRICE_CENTS (Feb 2026 refresh). 1, 3, 6, 12
// months are all live; the 12-month tier is highlighted as the best annual
// value ($39.99 — matches the annual self-subscription price).
const GIFT_TIERS = [
  {
    months: 1,
    label: "1 Month",
    priceCents: 399,
    tag: "A Gentle Introduction",
    description:
      "One month of daily wisdom and a private place to think — a caring first taste of LifeGuide.",
  },
  {
    months: 3,
    label: "3 Months",
    priceCents: 1197,
    tag: "Season of Reflection",
    description:
      "Three months of daily wisdom, quiet perspective, and a private place to turn during the holidays and beyond.",
  },
  {
    months: 6,
    label: "6 Months",
    priceCents: 2199,
    tag: "Deeper Roots",
    description:
      "Half a year of guidance for the seasons ahead — the new year, life changes, and the quiet moments in between.",
  },
  {
    months: 12,
    label: "12 Months",
    priceCents: 3999,
    tag: "Best Annual Value",
    featured: true,
    description:
      "A full year of encouragement, reflection, and support — a companion through every season of their life.",
  },
];

const price = (c) => `$${(c / 100).toFixed(2)}`;

// Best-effort timezone detection with fallback list. If the browser can't
// resolve a timezone we surface a picker (never guess).
const detectTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch (_) {
    return null;
  }
};

const COMMON_TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Anchorage",
  "America/Phoenix",
  "Pacific/Honolulu",
  "America/Toronto",
  "America/Vancouver",
  "Europe/London",
  "Europe/Berlin",
  "Europe/Paris",
  "Asia/Tokyo",
  "Australia/Sydney",
  "UTC",
];

// Combine a date + time (in the given IANA tz) into a UTC ISO string. If tz
// resolution fails, falls back to the purchaser's local timezone.
const buildSendAtIso = (dateStr, timeStr, tz) => {
  if (!dateStr || !timeStr) return null;
  // Naive local composition — the picker collects wall-clock time in the tz.
  const local = new Date(`${dateStr}T${timeStr}:00`);
  if (Number.isNaN(local.getTime())) return null;
  if (!tz) return local.toISOString();
  // Compute the tz offset for that moment using Intl.
  try {
    const utcMs = local.getTime() - local.getTimezoneOffset() * 60000;
    const asInTz = new Date(
      new Date(utcMs).toLocaleString("en-US", { timeZone: tz })
    ).getTime();
    const asLocal = new Date(
      new Date(utcMs).toLocaleString("en-US")
    ).getTime();
    const tzShiftMs = asLocal - asInTz;
    return new Date(local.getTime() + tzShiftMs).toISOString();
  } catch (_) {
    return local.toISOString();
  }
};

export default function GiftLandingPage() {
  const [selectedMonths, setSelectedMonths] = useState(12);
  const [purchaserName, setPurchaserName] = useState("");
  const [purchaserEmail, setPurchaserEmail] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [personalMessage, setPersonalMessage] = useState("");
  const [sendMode, setSendMode] = useState("now"); // "now" | "scheduled"
  const [sendDate, setSendDate] = useState("");
  const [sendTime, setSendTime] = useState("08:00");
  const detectedTz = useMemo(() => detectTimezone(), []);
  const [timezone, setTimezone] = useState(detectedTz || "");
  const [showTzPicker, setShowTzPicker] = useState(!detectedTz);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [busy, setBusy] = useState(false);

  // Always land at the top of the page — prevents the browser's scroll-restore
  // from dropping visitors partway down when they navigate to /gift.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const selected = GIFT_TIERS.find((t) => t.months === selectedMonths);
  const needsTzPicker = showTzPicker || !detectedTz;

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;

    // Validate scheduled fields when scheduled
    let sendAtIso = null;
    if (sendMode === "scheduled") {
      if (!sendDate) return toast.error("Please choose a delivery date.");
      if (!sendTime) return toast.error("Please choose a delivery time.");
      if (!timezone)
        return toast.error(
          "Please choose the recipient's timezone so we deliver on time."
        );
      sendAtIso = buildSendAtIso(sendDate, sendTime, timezone);
      if (!sendAtIso) return toast.error("The delivery date/time is invalid.");
      if (new Date(sendAtIso).getTime() <= Date.now()) {
        return toast.error("The delivery date/time must be in the future.");
      }
    }

    setBusy(true);
    try {
      const { url } = await gifts.guestCreate({
        purchaser_name: purchaserName.trim(),
        purchaser_email: purchaserEmail.trim().toLowerCase(),
        recipient_name: recipientName.trim(),
        recipient_email: recipientEmail.trim().toLowerCase(),
        duration_months: selectedMonths,
        is_anonymous: isAnonymous,
        message: personalMessage.trim() || null,
        send_at: sendAtIso,
        recipient_timezone: sendMode === "scheduled" ? timezone : null,
        origin_url: window.location.origin,
      });
      if (!url) {
        toast.error("Could not start gift checkout.");
        setBusy(false);
        return;
      }
      // Prefer top-frame nav (works inside iframed previews too).
      try {
        if (window.top && window.top !== window.self) {
          window.top.location.href = url;
          return;
        }
      } catch (_) {}
      window.location.href = url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start gift checkout.");
      setBusy(false);
    }
  };

  const scrollToId = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen text-[#2E2E2E]">
      {/* -------- Top nav -------- */}
      <header className="border-b border-[#DCE5DB]/70 bg-white/70 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 sm:gap-4">
            <BackToLifeGuide testid="gift-back-to-lifeguide" />
            <Link to="/" className="flex items-center gap-2.5" data-testid="gift-landing-home-link">
              <div className="w-9 h-9 rounded-2xl bg-[#4F6F52] flex items-center justify-center shadow-[0_4px_14px_rgba(79,111,82,0.25)]">
                <Sparkles size={16} className="text-[#F9FAF7]" strokeWidth={1.8} />
              </div>
              <span className="font-display text-xl text-[#4F6F52]">LifeGuide</span>
            </Link>
          </div>
          <div className="hidden sm:flex items-center gap-6 text-sm text-[#6B6B6B]">
            <Link to="/login" className="hover:text-[#4F6F52]" data-testid="gift-landing-signin">
              Sign in
            </Link>
            <a
              href="#gift-tiers"
              onClick={(e) => { e.preventDefault(); scrollToId("gift-tiers"); }}
              className="bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full px-4 py-2 transition"
              data-testid="gift-landing-nav-cta"
            >
              Give LifeGuide
            </a>
          </div>
        </div>
      </header>

      {/* -------- Hero -------- */}
      <section className="max-w-4xl mx-auto px-6 pt-16 sm:pt-24 pb-14 sm:pb-20 text-center fade-in-up">
        <div className="inline-flex items-center gap-2 rounded-full bg-[#F5EBD9]/80 border border-[#D6C7A1]/60 px-4 py-1.5 mb-6">
          <Gift size={14} className="text-[#8C7A4F]" />
          <span className="text-xs uppercase tracking-[0.18em] text-[#8C7A4F] font-semibold">
            Holiday Gift
          </span>
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#2E2E2E] mb-6">
          Give More Than a Gift.
          <br />
          <span className="text-[#4F6F52]">Give Someone a Place to Turn.</span>
        </h1>
        <p className="text-lg sm:text-xl text-[#6B6B6B] leading-relaxed max-w-2xl mx-auto mb-10">
          LifeGuide gives someone you care about a private place to find
          perspective, encouragement, and practical guidance whenever life
          feels difficult, confusing, or uncertain.
        </p>
        <a
          href="#gift-tiers"
          onClick={(e) => { e.preventDefault(); scrollToId("gift-tiers"); }}
          data-testid="gift-landing-hero-cta"
          className="inline-flex items-center gap-2 bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full px-8 py-4 font-medium transition shadow-[0_6px_20px_rgba(79,111,82,0.25)]"
        >
          Give LifeGuide
          <ArrowRight size={16} strokeWidth={2} />
        </a>
      </section>

      {/* -------- Message block -------- */}
      <section className="max-w-3xl mx-auto px-6 pb-16 sm:pb-24">
        <div className="rounded-3xl bg-white/70 backdrop-blur-sm border border-[#DCE5DB] p-8 sm:p-12 text-center shadow-[0_4px_20px_rgba(0,0,0,0.03)]">
          <MessageCircleHeart
            size={26}
            className="text-[#4F6F52] mx-auto mb-5"
            strokeWidth={1.6}
          />
          <p className="font-display text-2xl sm:text-3xl text-[#2E2E2E] leading-snug">
            Sometimes you need someone to help you think it through.
            <br className="hidden sm:block" />
            <span className="text-[#6B6B6B]"> Sometimes you simply need a word of wisdom.</span>
            <br />
            <span className="text-[#4F6F52]">LifeGuide gives you both.</span>
          </p>
        </div>
      </section>

      {/* -------- Gift tiers -------- */}
      <section id="gift-tiers" className="max-w-6xl mx-auto px-6 pb-16 sm:pb-24 scroll-mt-24">
        <div className="text-center mb-12">
          <div className="text-xs uppercase tracking-[0.2em] text-[#7FA7B0] font-semibold mb-3">
            Choose Their Season
          </div>
          <h2 className="font-display text-3xl sm:text-4xl text-[#2E2E2E]">
            A gift that keeps giving, quietly.
          </h2>
          <p className="text-sm text-[#6B6B6B] mt-3 max-w-md mx-auto">
            Pick the length of the gift below. We'll show your total on the next step.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {GIFT_TIERS.map((tier) => {
            const active = selectedMonths === tier.months;
            return (
              <button
                type="button"
                key={tier.months}
                onClick={() => {
                  setSelectedMonths(tier.months);
                  // Smoothly scroll to the form so the shopper sees the selected
                  // duration & price highlighted before filling in details.
                  requestAnimationFrame(() => scrollToId("gift-form"));
                }}
                data-testid={`gift-tier-${tier.months}`}
                className={`relative text-left rounded-3xl border p-7 transition ${
                  tier.featured
                    ? "bg-gradient-to-br from-[#F5EBD9]/60 to-white"
                    : "bg-white"
                } ${
                  active
                    ? "border-[#4F6F52] shadow-[0_10px_30px_rgba(79,111,82,0.18)] -translate-y-0.5"
                    : "border-[#DCE5DB] hover:border-[#4F6F52]/40 hover:-translate-y-0.5"
                }`}
              >
                {tier.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#8C7A4F] text-white text-[11px] uppercase tracking-[0.15em] font-semibold px-3 py-1 rounded-full shadow-[0_4px_12px_rgba(140,122,79,0.28)]">
                    {tier.tag}
                  </div>
                )}
                {!tier.featured && (
                  <div className="text-xs uppercase tracking-[0.16em] text-[#7FA7B0] font-semibold mb-2">
                    {tier.tag}
                  </div>
                )}
                <div className="flex items-baseline justify-between mb-3">
                  <div className="font-display text-2xl text-[#2E2E2E]">
                    {tier.label}
                  </div>
                  <div className={`font-display text-2xl ${tier.featured ? "text-[#8C7A4F]" : "text-[#4F6F52]"}`}>
                    {price(tier.priceCents)}
                  </div>
                </div>
                <p className="text-sm text-[#6B6B6B] leading-relaxed mb-5">
                  {tier.description}
                </p>
                <div
                  className={`inline-flex items-center gap-1.5 text-sm font-medium ${
                    active ? "text-[#4F6F52]" : "text-[#6B6B6B]"
                  }`}
                >
                  {active ? (
                    <>
                      <Check size={14} strokeWidth={2.2} />
                      Selected
                    </>
                  ) : (
                    <>
                      Choose this
                      <ArrowRight size={14} strokeWidth={2} />
                    </>
                  )}
                </div>
              </button>
            );
          })}
        </div>
        <p className="text-center text-xs text-[#6B6B6B] mt-6 max-w-xl mx-auto leading-relaxed">
          One-time gift. The recipient's access ends after the term you choose —
          they'll never be auto-charged and can continue on their own only if
          they choose to.
        </p>
      </section>

      {/* -------- Free wisdom section -------- */}
      <section className="bg-[#EEF3EC]/60 border-y border-[#DCE5DB] py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-6 grid md:grid-cols-[auto,1fr] gap-8 items-start">
          <div className="w-14 h-14 rounded-2xl bg-white border border-[#DCE5DB] flex items-center justify-center shrink-0 shadow-[0_4px_14px_rgba(79,111,82,0.08)]">
            <BookOpen size={24} className="text-[#4F6F52]" strokeWidth={1.6} />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#7FA7B0] font-semibold mb-2">
              Always Free
            </div>
            <h2 className="font-display text-2xl sm:text-3xl text-[#2E2E2E] mb-4">
              Biblical wisdom is always included.
            </h2>
            <p className="text-[#3B3B3B] leading-relaxed mb-4">
              Every LifeGuide user has easy access to{" "}
              <strong className="text-[#4F6F52]">I Need a Proverb</strong> — a
              free resource for finding biblical wisdom based on what they're
              facing right now. A quiet moment, a searching question, and a
              proverb that meets them there.
            </p>
            <p className="text-[#6B6B6B] leading-relaxed">
              I Need a Proverb <strong>remains free forever</strong> — even
              after a LifeGuide gift subscription ends. Wisdom shouldn't have
              an expiration date.
            </p>
          </div>
        </div>
      </section>

      {/* -------- Gift purchase form -------- */}
      <section id="gift-form" className="max-w-3xl mx-auto px-6 py-16 sm:py-24 scroll-mt-24">
        <div className="text-center mb-10">
          <div className="text-xs uppercase tracking-[0.2em] text-[#7FA7B0] font-semibold mb-3">
            Send Your Gift
          </div>
          <h2 className="font-display text-3xl sm:text-4xl text-[#2E2E2E] mb-3">
            Give LifeGuide
          </h2>
          <p className="text-[#6B6B6B] leading-relaxed max-w-xl mx-auto">
            A one-time gift. No auto-renewal. Delivered by beautiful branded email —
            on your schedule.
          </p>
        </div>

        <form
          onSubmit={submit}
          className="bg-white rounded-3xl border border-[#DCE5DB] p-6 sm:p-9 space-y-6 shadow-[0_6px_30px_rgba(0,0,0,0.04)]"
          data-testid="gift-landing-form"
        >
          {/* Selected tier summary */}
          <div className="rounded-2xl bg-[#F5EBD9]/50 border border-[#D6C7A1]/50 px-4 py-3.5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Gift size={16} className="text-[#8C7A4F]" />
              <div>
                <div className="text-sm font-semibold text-[#2E2E2E]">
                  {selected.label} of LifeGuide
                </div>
                <div className="text-xs text-[#6B6B6B]">{selected.tag}</div>
              </div>
            </div>
            <div className="font-display text-xl text-[#8C7A4F]">
              {price(selected.priceCents)}
            </div>
          </div>

          {/* Purchaser */}
          <div>
            <div className="text-xs uppercase tracking-[0.16em] text-[#6B6B6B] font-semibold mb-3">
              From You
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[#6B6B6B] mb-1.5">Your name</label>
                <input
                  data-testid="gift-purchaser-name"
                  required
                  value={purchaserName}
                  onChange={(e) => setPurchaserName(e.target.value)}
                  className="w-full bg-[#F9FAF7] border border-[#DCE5DB] rounded-2xl px-4 py-3 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition"
                  placeholder="Your first name"
                />
              </div>
              <div>
                <label className="block text-sm text-[#6B6B6B] mb-1.5">Your email</label>
                <input
                  data-testid="gift-purchaser-email"
                  type="email"
                  required
                  value={purchaserEmail}
                  onChange={(e) => setPurchaserEmail(e.target.value)}
                  className="w-full bg-[#F9FAF7] border border-[#DCE5DB] rounded-2xl px-4 py-3 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition"
                  placeholder="you@example.com"
                />
              </div>
            </div>
          </div>

          {/* Recipient */}
          <div>
            <div className="text-xs uppercase tracking-[0.16em] text-[#6B6B6B] font-semibold mb-3">
              For Them
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-[#6B6B6B] mb-1.5">Their name</label>
                <input
                  data-testid="gift-recipient-name"
                  required
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  className="w-full bg-[#F9FAF7] border border-[#DCE5DB] rounded-2xl px-4 py-3 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition"
                  placeholder="Their first name"
                />
              </div>
              <div>
                <label className="block text-sm text-[#6B6B6B] mb-1.5">Their email</label>
                <input
                  data-testid="gift-recipient-email"
                  type="email"
                  required
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  className="w-full bg-[#F9FAF7] border border-[#DCE5DB] rounded-2xl px-4 py-3 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition"
                  placeholder="them@example.com"
                />
              </div>
            </div>
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm text-[#6B6B6B] mb-1.5">
              Personal message <span className="text-[#6B6B6B]/70">(optional)</span>
            </label>
            <textarea
              data-testid="gift-personal-message"
              value={personalMessage}
              onChange={(e) => setPersonalMessage(e.target.value)}
              rows={3}
              maxLength={500}
              className="w-full bg-[#F9FAF7] border border-[#DCE5DB] rounded-2xl px-4 py-3 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition resize-none"
              placeholder={`A short note they'll read when they open the gift.`}
            />
          </div>

          {/* Send schedule */}
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-[#6B6B6B] font-semibold mb-3">
              <CalendarClock size={13} />
              When to send
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                data-testid="gift-send-now"
                onClick={() => setSendMode("now")}
                className={`rounded-2xl border px-4 py-3 text-sm font-medium transition ${
                  sendMode === "now"
                    ? "border-[#4F6F52] bg-[#E1ECDD] text-[#4F6F52]"
                    : "border-[#DCE5DB] bg-white text-[#2E2E2E] hover:border-[#4F6F52]/40"
                }`}
              >
                Send now
              </button>
              <button
                type="button"
                data-testid="gift-send-scheduled"
                onClick={() => setSendMode("scheduled")}
                className={`rounded-2xl border px-4 py-3 text-sm font-medium transition ${
                  sendMode === "scheduled"
                    ? "border-[#4F6F52] bg-[#E1ECDD] text-[#4F6F52]"
                    : "border-[#DCE5DB] bg-white text-[#2E2E2E] hover:border-[#4F6F52]/40"
                }`}
              >
                Send on a date
              </button>
            </div>
            {sendMode === "scheduled" && (
              <div className="mt-3 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <input
                    data-testid="gift-send-date"
                    type="date"
                    value={sendDate}
                    onChange={(e) => setSendDate(e.target.value)}
                    min={new Date().toISOString().split("T")[0]}
                    className="w-full bg-[#F9FAF7] border border-[#DCE5DB] rounded-2xl px-4 py-3 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition"
                  />
                  <input
                    data-testid="gift-send-time"
                    type="time"
                    value={sendTime}
                    onChange={(e) => setSendTime(e.target.value)}
                    className="w-full bg-[#F9FAF7] border border-[#DCE5DB] rounded-2xl px-4 py-3 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.14em] text-[#6B6B6B] font-semibold mb-1.5">
                    Recipient's timezone
                  </label>
                  {needsTzPicker ? (
                    <select
                      data-testid="gift-timezone-picker"
                      value={timezone}
                      onChange={(e) => setTimezone(e.target.value)}
                      className="w-full bg-[#F9FAF7] border border-[#DCE5DB] rounded-2xl px-4 py-3 focus:outline-none focus:border-[#4F6F52] focus:ring-2 focus:ring-[#4F6F52]/15 transition"
                      required
                    >
                      <option value="">Select their timezone…</option>
                      {COMMON_TIMEZONES.map((tz) => (
                        <option key={tz} value={tz}>{tz.replace(/_/g, " ")}</option>
                      ))}
                    </select>
                  ) : (
                    <div
                      data-testid="gift-timezone-detected"
                      className="flex items-center gap-2 text-sm text-[#3B3B3B] rounded-2xl bg-[#EEF3EC] border border-[#DCE5DB] px-4 py-3"
                    >
                      <CalendarClock size={13} className="text-[#4F6F52]" />
                      <span>Default: {timezone.replace(/_/g, " ")}</span>
                      <button
                        type="button"
                        onClick={() => { setShowTzPicker(true); setTimezone(""); }}
                        data-testid="gift-timezone-change"
                        className="ml-auto text-xs text-[#4F6F52] underline underline-offset-4"
                      >
                        Change
                      </button>
                    </div>
                  )}
                  <p className="text-xs text-[#6B6B6B] mt-1.5 leading-relaxed">
                    We'll deliver the gift email at {sendTime || "8:00 AM"} in
                    this timezone. Default is 8:00 AM local — feel free to adjust.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Anonymous toggle */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              data-testid="gift-anonymous"
              type="checkbox"
              checked={isAnonymous}
              onChange={(e) => setIsAnonymous(e.target.checked)}
              className="mt-1 w-4 h-4 accent-[#4F6F52]"
            />
            <span className="text-sm text-[#2E2E2E] leading-relaxed inline-flex items-center gap-1.5">
              <EyeOff size={13} className="text-[#6B6B6B]" />
              <strong className="font-medium">Send anonymously.</strong>
              <span className="text-[#6B6B6B]">
                They'll see: "Someone who cares about you gave you LifeGuide."
              </span>
            </span>
          </label>

          {/* Submit */}
          <button
            data-testid="gift-landing-submit"
            type="submit"
            disabled={busy}
            className="w-full bg-[#4F6F52] hover:bg-[#3F5A42] disabled:opacity-60 text-white rounded-full py-4 font-medium transition inline-flex items-center justify-center gap-2 shadow-[0_6px_20px_rgba(79,111,82,0.22)]"
          >
            <Heart size={15} strokeWidth={2} />
            {busy
              ? "Opening secure checkout…"
              : `Give LifeGuide — ${price(selected.priceCents)}`}
            {!busy && <ArrowRight size={15} strokeWidth={2} />}
          </button>
          <p className="text-xs text-[#6B6B6B] text-center leading-relaxed">
            Secure one-time payment. No auto-renewal for the recipient.
          </p>
        </form>
      </section>

      {/* -------- Footer -------- */}
      <footer className="border-t border-[#DCE5DB] py-10 bg-white/50">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-[#6B6B6B]">
          <div className="flex items-center gap-2">
            <Mail size={12} />
            <Link to="/contact" className="hover:text-[#4F6F52]" data-testid="gift-contact-link">
              Contact us
            </Link>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/privacy" className="hover:text-[#4F6F52]" data-testid="legal-privacy-link">
              Privacy
            </Link>
            <Link to="/terms" className="hover:text-[#4F6F52]" data-testid="legal-terms-link">
              Terms
            </Link>
            <Link to="/" className="hover:text-[#4F6F52]">
              LifeGuide
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
